import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { Book, Code, Hash, Database, List, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const docSections = [
  {
    title: 'Guides',
    description: 'Step-by-step tutorials and how-to articles for getting started and advanced usage.',
    href: '/docs/guides',
    icon: Book,
    color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  },
  {
    title: 'API Reference',
    description: 'Technical documentation for integrating with the AutomotiveAPI standards API.',
    href: '/docs/api',
    icon: Code,
    color: 'bg-green-500/10 text-green-600 dark:text-green-400',
  },
  {
    title: 'Data Types',
    description: 'Comprehensive reference for formats, types, and data structures used in standards.',
    href: '/docs/data-types',
    icon: Hash,
    color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  },
  {
    title: 'Field Library',
    description: 'Reusable field definitions with validation rules and constraints.',
    href: '/docs/field-library',
    icon: Database,
    color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  },
  {
    title: 'Lookups',
    description: 'Enumerated value tables for standardized options and code lists.',
    href: '/docs/lookups',
    icon: List,
    color: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
  },
];

export default function DocsHub() {
  return (
    <Layout>
      <SEOHead path="/docs" />
      
      <DocsNav />
      
      <div className="container py-12 md:py-16">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
            <Book className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Documentation
          </h1>
          <p className="text-lg text-muted-foreground">
            Everything you need to understand and implement automotive data standards. 
            From getting started guides to complete API reference.
          </p>
        </div>
        
        {/* Cards Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
          {docSections.map((section) => {
            const Icon = section.icon;
            return (
              <Link key={section.href} to={section.href} className="group">
                <Card className="h-full transition-all hover:shadow-md hover:border-primary/50">
                  <CardHeader>
                    <div className={cn('inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4', section.color)}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="flex items-center gap-2">
                      {section.title}
                      <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                    </CardTitle>
                    <CardDescription className="text-sm">
                      {section.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
        
        {/* Quick Links */}
        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Looking for something specific?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/api-standards" 
              className="text-sm text-primary hover:underline"
            >
              Browse API Standards →
            </Link>
            <Link 
              to="/api-standards/builder" 
              className="text-sm text-primary hover:underline"
            >
              Schema Builder →
            </Link>
            <Link 
              to="/proposals" 
              className="text-sm text-primary hover:underline"
            >
              Community Proposals →
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
